import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import * as Select from "@radix-ui/react-select";
import { trackEvent } from '../../lib/analytics';

const languages = [
  { code: 'en', label: 'English', flag: 'gb' },
  { code: 'de', label: 'Deutsch', flag: 'de' },
  { code: 'pl', label: 'Polski', flag: 'pl' },
  { code: 'nl', label: 'Nederlands', flag: 'nl' },
  { code: 'cz', label: 'Čeština', flag: 'cz' },
  { code: 'at', label: 'Deutsch (AT)', flag: 'at' },
  { code: 'hu', label: 'Magyar', flag: 'hu' },
  { code: 'fr', label: 'Français', flag: 'fr' },
  { code: 'it', label: 'Italiano', flag: 'it' },
  { code: 'sk', label: 'Slovenčina', flag: 'sk' },
  { code: 'ro', label: 'Română', flag: 'ro' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // i18n.language can be a full locale (e.g. "en-GB"); normalize to the base code.
  const currentCode = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const currentLang = languages.find((l) => l.code === currentCode) ?? languages[0];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path.startsWith("/#")) return location.hash === path.replace("/", "");
    return location.pathname === path;
  };

  const closeMenu = () => setMenuOpen(false);

  const closeMenuAndScrollTop = () => {
    setMenuOpen(false);
    window.scrollTo({ top: 0 });
  };

  const scrollToCountries = useCallback(() => {
    closeMenu();
    if (location.pathname === "/") {
      const el = document.getElementById("countries");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#countries");
    }
  }, [location.pathname, navigate]);

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-200 ${
          scrolled
            ? "bg-surface/90 backdrop-blur-md border-b border-border"
            : "bg-surface border-b border-transparent"
        }`}
      >
        {/* Header row */}
        <div className="mx-auto flex max-w-5xl items-center justify-between px-2 md:px-5 py-3.5">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" onClick={closeMenuAndScrollTop}>
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary">
              <i className="ri-ticket-line text-white text-[16px]" />
            </div>
            <span className="text-[16px] font-semibold tracking-tight text-ink">
              e-vignette
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden mobile:flex items-center gap-7 text-[14px] font-medium text-muted">
            <button
              onClick={scrollToCountries}
              className={`transition hover:text-primary cursor-pointer ${isActive("/#countries") ? "text-primary" : ""}`}
            >
              {t('header.nav.countries')}
            </button>
            <Link
              to="/how-it-works"
              className={`transition hover:text-primary ${isActive("/how-it-works") ? "text-primary" : ""}`}
            >
              {t('header.nav.howItWorks')}
            </Link>
            <Link
              to="/blog"
              className={`transition hover:text-primary ${isActive("/blog") ? "text-primary" : ""}`}
            >
              {t('header.nav.blog')}
            </Link>
            <Link
              to="/contact"
              className={`transition hover:text-primary ${isActive("/contact") ? "text-primary" : ""}`}
            >
              {t('header.nav.contact')}
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            {/* Language switcher — always visible */}
            <Select.Root value={currentLang.code} onValueChange={(code) => {
              trackEvent('language_change', { language_from: currentLang.code, language_to: code });
              i18n.changeLanguage(code);
            }}>
              <Select.Trigger className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1.5 text-[13px] font-medium text-ink outline-none transition hover:border-primary cursor-pointer">
                <img
                  src={`https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/${currentLang.flag}.svg`}
                  width={20}
                  height={14}
                  className="rounded-sm object-cover"
                />
                <span>{currentLang.code.toUpperCase()}</span>
                <Select.Icon>
                  <i className="ri-arrow-down-s-line text-muted text-[14px]" />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  position="popper"
                  align="end"
                  sideOffset={8}
                  className="z-50 min-w-[180px] overflow-hidden rounded-xl border border-border bg-white shadow-lg"
                >
                  <Select.Viewport className="p-1">
                    {languages.map((lang) => (
                      <Select.Item
                        key={lang.code}
                        value={lang.code}
                        className="relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] text-ink outline-none cursor-pointer select-none data-[highlighted]:bg-bg data-[state=checked]:text-primary"
                      >
                        <img
                          src={`https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/${lang.flag}.svg`}
                          width={20}
                          height={14}
                          className="rounded-sm object-cover"
                        />
                        <Select.ItemText>{lang.label}</Select.ItemText>
                        <Select.ItemIndicator className="ml-auto">
                          <i className="ri-check-line text-primary text-[16px]" />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>

            {/* Desktop CTA */}
            <div className="hidden mobile:flex">
              <button
                onClick={() => { trackEvent('cta_click', { cta_name: 'choose_country', cta_location: 'header' }); scrollToCountries(); }}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-line text-[14px]" />
                {t('header.cta')}
              </button>
            </div>

            {/* Burger button — mobile only */}
            <button
              className="mobile:hidden relative flex h-8 w-8 items-center justify-center rounded-md transition hover:bg-bg"
              onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              <span
                className={`absolute block h-[2px] w-5 rounded-full bg-ink transition-all duration-300 ease-in-out origin-center ${
                  menuOpen ? 'rotate-45 translate-y-0' : '-translate-y-[6px]'
                }`}
              />
              <span
                className={`absolute block h-[2px] w-5 rounded-full bg-ink transition-all duration-300 ease-in-out ${
                  menuOpen ? 'opacity-0 scale-x-0' : 'opacity-100 scale-x-100'
                }`}
              />
              <span
                className={`absolute block h-[2px] w-5 rounded-full bg-ink transition-all duration-300 ease-in-out origin-center ${
                  menuOpen ? '-rotate-45 translate-y-0' : 'translate-y-[6px]'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Mobile menu — slides open below header */}
        <div
          className={`mobile:hidden overflow-hidden border-t transition-all duration-300 ease-in-out ${
            menuOpen
              ? 'max-h-[500px] opacity-100 border-border'
              : 'max-h-0 opacity-0 border-transparent'
          }`}
        >
          <nav className="flex flex-col px-4 pb-6 pt-2">
            <button
              onClick={scrollToCountries}
              className="flex items-center gap-3.5 rounded-xl px-3 py-3.5 text-[15px] font-medium text-ink-soft transition hover:bg-bg hover:text-primary text-left"
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                location.pathname === '/' ? 'bg-primary-soft text-primary' : 'bg-bg text-muted'
              }`}>
                <i className="ri-map-pin-line text-[16px]" />
              </span>
              {t('header.nav.countries')}
            </button>

            <Link
              to="/how-it-works"
              onClick={closeMenuAndScrollTop}
              className="flex items-center gap-3.5 rounded-xl px-3 py-3.5 text-[15px] font-medium text-ink-soft transition hover:bg-bg hover:text-primary"
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                isActive('/how-it-works') ? 'bg-primary-soft text-primary' : 'bg-bg text-muted'
              }`}>
                <i className="ri-information-line text-[16px]" />
              </span>
              {t('header.nav.howItWorks')}
            </Link>

            <Link
              to="/blog"
              onClick={closeMenuAndScrollTop}
              className="flex items-center gap-3.5 rounded-xl px-3 py-3.5 text-[15px] font-medium text-ink-soft transition hover:bg-bg hover:text-primary"
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                isActive('/blog') ? 'bg-primary-soft text-primary' : 'bg-bg text-muted'
              }`}>
                <i className="ri-article-line text-[16px]" />
              </span>
              {t('header.nav.blog')}
            </Link>

            <Link
              to="/contact"
              onClick={closeMenuAndScrollTop}
              className="flex items-center gap-3.5 rounded-xl px-3 py-3.5 text-[15px] font-medium text-ink-soft transition hover:bg-bg hover:text-primary"
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                isActive('/contact') ? 'bg-primary-soft text-primary' : 'bg-bg text-muted'
              }`}>
                <i className="ri-mail-line text-[16px]" />
              </span>
              {t('header.nav.contact')}
            </Link>

            <div className="mt-4 h-px bg-border" />

            <button
              onClick={() => { trackEvent('cta_click', { cta_name: 'choose_country', cta_location: 'header' }); scrollToCountries(); }}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-[14px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer"
            >
              <i className="ri-map-pin-line text-[15px]" />
              {t('header.cta')}
            </button>
          </nav>
        </div>
      </header>

      {/* Backdrop — closes menu on click */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 mobile:hidden transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMenuAndScrollTop}
      />
    </>
  );
}
